/**
 * Decides WHEN a single `mat-select` pick takes effect, for a pick that does something more than
 * write the model (a PUT, a confirm dialog).
 *
 * A pick has to be read off the select's `(selectionChange)`, never an option's `(click)`: an
 * option chosen with Enter fires no DOM click, so a `(click)` handler served a pointer only. But on
 * a CLOSED single select Material also selects on every arrow key and every type-ahead letter
 * (`MatSelect._handleClosedKeydown` -> key manager `change` -> `_selectViaInteraction()`), and each
 * of those is a user pick that emits `selectionChange`. Acting on every one turned walking the list
 * into a PUT per key press, racing each other, or a confirm dialog on the first step past the
 * current value.
 *
 * So a pick made while the panel is OPEN -- a click, or Enter on the active option -- takes effect
 * at once, and a pick made while it is CLOSED is held until the select loses focus, when the user
 * has stopped walking. Blur is skipped while the panel is open: clicking an option moves focus
 * into the overlay first, and that blur is not the user leaving the control.
 *
 * So the panel CLOSING settles too (`panelClosed()`, from `(openedChange)` false). A held pick
 * whose select is then opened and dismissed by a backdrop click is otherwise never committed: the
 * backdrop's mousedown blurs the select while the panel is still open, and `MatSelect.close()`
 * gives no focus back, so the blur `settle()` waits for never comes (MR !217 review).
 *
 * Wire it to a select with `SettleClosedPickDirective` (`<mat-select [appSettleClosedPick]="pick">`),
 * never by hand: four bindings each have to pass the right argument, and a missed one silently
 * brings back either the per-key commit or a lost pick.
 */
/** The part of `MatSelect` that `keydown()` reads: its type-ahead, and its panel. */
export interface TypeaheadOwner {
  _keyManager?: { isTyping(): boolean } | null;
  typeaheadDebounceInterval?: number;
  panelOpen?: boolean;
  close?(): void;
}

/** CDK's own default when a select states no `typeaheadDebounceInterval` (`ListKeyManager.withTypeAhead`). */
const DEFAULT_TYPEAHEAD_MS = 200;
/** Slack past the type-ahead's debounce for its pick to land, before an armed Enter stops waiting. */
const TYPEAHEAD_LAND_SLACK_MS = 250;

export class ClosedSelectPick<T> {
  private pending: { value: T } | null = null;
  private pickCount = 0;
  /**
   * Until when a closed pick commits at once rather than being held: set by an Enter pressed while a
   * type-ahead is still pending, so the option the letters were looking for is chosen as Enter asked.
   */
  private commitTypeaheadUntil = 0;
  /** Ends an armed Enter's wait: commits what is held if the type-ahead landed no new pick. */
  private armedTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(private readonly commit: (value: T) => void) {}

  /** From the select's `(selectionChange)`, which Material emits BEFORE it closes the panel on a pick. */
  picked(value: T, panelOpen: boolean): void {
    this.pickCount++;
    if (panelOpen || this.typeaheadArmed()) {
      this.disarm();
      this.pending = null;
      this.commit(value);
    } else {
      this.pending = { value };
    }
  }

  /** From the select's `(blur)`. */
  settle(panelOpen: boolean): void {
    if (panelOpen || !this.pending) return;
    const { value } = this.pending;
    this.pending = null;
    this.commit(value);
  }

  /** From the select's `(openedChange)` when it reports `false`. */
  panelClosed(): void {
    this.flush();
  }

  /**
   * Commit a held pick whatever the panel is doing, for a host that is going away. The select has
   * been showing the held value since the key press, so dropping it on destroy would leave the user
   * believing a switch happened that never ran (MR !217 review). Removing a focused node does not
   * reliably fire blur, so `settle()` cannot be relied on here.
   */
  flush(): boolean {
    if (!this.pending) return false;
    const { value } = this.pending;
    this.pending = null;
    this.commit(value);
    return true;
  }

  /**
   * From the select's `(keydown)`, after Material's own handler has run (see the directive).
   *
   * Enter pressed while a type-ahead is still waiting on its debounce is "choose what I typed":
   * Material neither opens the panel nor picks on that Enter (`_handleClosedKeydown` checks
   * `isTyping()`), and ~200ms later the debounce selects the typed option as a closed pick. That
   * pick is committed as soon as it lands rather than held for a blur. Cancelling the type-ahead
   * instead (as this once did) made "type a letter, press Enter" select nothing at all (MR !217
   * review). If the wait runs out with no pick -- the letters matched nothing, or landed back on the
   * option already selected, for which CDK emits no change -- Enter still commits what the select is
   * showing: a pick held from an earlier walk was otherwise left waiting for a blur (MR !217 review).
   * A host passing `flushOnEnter = false` keeps the pick held, as for any closed pick.
   *
   * Otherwise `flushOnEnter` ends the walk at once: a held pick commits on Enter rather than on blur.
   * Material's own keydown is a HOST listener and has already run, so on a closed select the panel
   * is opening; a host whose commit opens a dialog (the buylist brand confirm) passes `false` and
   * lets the panel's close settle it instead, rather than stacking a dialog over an open panel.
   *
   * An Enter that commits a held pick then CLOSES the panel its own keydown just opened: the pick is
   * made, and a list left open over the form turned the next arrow or letter into a change of the
   * next entry (MR !217 review).
   */
  keydown(event: KeyboardEvent, select?: TypeaheadOwner, flushOnEnter = true): void {
    if (event.key !== 'Enter') {
      return;
    }
    if (select?._keyManager?.isTyping()) {
      if (flushOnEnter) {
        const waitMs = (select.typeaheadDebounceInterval ?? DEFAULT_TYPEAHEAD_MS) + TYPEAHEAD_LAND_SLACK_MS;
        this.disarm();
        this.commitTypeaheadUntil = Date.now() + waitMs;
        this.armedTimer = setTimeout(() => {
          this.armedTimer = null;
          this.commitTypeaheadUntil = 0;
          this.flush();
        }, waitMs);
      }
      return;
    }
    if (flushOnEnter && this.flush() && select?.panelOpen) {
      select.close?.();
    }
  }

  /**
   * From the select's keydown in the CAPTURE phase, i.e. before Material handles it: any other key
   * after an armed Enter means the user moved on, so the next closed pick is a walk step again.
   */
  keydownCapture(event: KeyboardEvent): void {
    if (event.key !== 'Enter') {
      this.disarm();
    }
  }

  private disarm(): void {
    this.commitTypeaheadUntil = 0;
    if (this.armedTimer !== null) {
      clearTimeout(this.armedTimer);
      this.armedTimer = null;
    }
  }

  private typeaheadArmed(): boolean {
    return this.commitTypeaheadUntil !== 0 && Date.now() <= this.commitTypeaheadUntil;
  }

  /**
   * Drop a held pick without committing it, for a host whose model has moved under the walk -- the
   * server answered with the current value, the form was reset, the session changed hands. The
   * select is showing the newer value by then, so committing the walk afterwards would act on a
   * value the user can no longer see (MR !217 review).
   */
  discard(): void {
    this.pending = null;
    this.disarm();
  }

  /**
   * The select is gone: cancel an armed Enter's timed commit, and leave any held pick to the host.
   * The timer would otherwise fire up to ~450ms after the select was destroyed and commit into a
   * host that may be gone too (MR !217 review). What happens to the held pick is the HOST's call --
   * `SelectWarehouseComponent` flushes it from its own `ngOnDestroy`, `DefaultSelectComponent`
   * drops it -- and the directive is destroyed before the host, so this must not decide it.
   */
  dispose(): void {
    this.disarm();
  }

  /**
   * How many picks this select has reported, held or committed. A host that sends a request and
   * must not let its answer overwrite a pick made WHILE it was on the wire stamps this when it asks.
   */
  get picks(): number {
    return this.pickCount;
  }

  get hasPending(): boolean {
    return this.pending !== null;
  }

  /**
   * The pick held on the closed select, if any -- boxed, so a held `null` is not "nothing held". A
   * COPY: handing out the internal box let a caller change what `flush()` commits without a pick
   * being counted (MR !222 review, round 5).
   */
  get held(): Readonly<{ value: T }> | null {
    return this.pending ? { value: this.pending.value } : null;
  }
}
